import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, BedDouble, Bath, Ruler } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const imgOf = (l) => {
  const im = l.image;
  if (!im) return null;
  if (typeof im === "string") return im.startsWith("//") ? `https:${im}` : im;
  if (im.url) return im.url.startsWith("//") ? `https:${im.url}` : im.url;
  const entries = Object.keys(im)
    .filter((k) => !isNaN(Number(k)))
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => im[k])
    .filter((v) => v && typeof v === "object");
  const first = entries[0];
  if (!first) return null;
  const u = first.url || first.urls?.hires?.url || first.urls?.thumb?.url || first.sizes?.thumb;
  return u ? (u.startsWith("//") ? `https:${u}` : u) : null;
};

const hrefOf = (l) => (l.listingID ? `/listing/${l.listingID}` : "/listings");

const LAYOUTS = [
  { cls: "lg:col-span-7", aspect: "aspect-[16/11]" },
  { cls: "lg:col-span-5 lg:mt-24", aspect: "aspect-[4/5]" },
  { cls: "lg:col-span-5", aspect: "aspect-[4/5]" },
  { cls: "lg:col-span-7 lg:-mt-16", aspect: "aspect-[16/11]" },
  { cls: "lg:col-span-6 lg:col-start-4 lg:mt-8", aspect: "aspect-[16/10]" },
];

const SoldShowcase = ({ limit = 8 }) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let live = true;
    axios
      .get(`${API}/idx/soldpending`, { params: { limit } })
      .then((r) => {
        if (live) setItems(Array.isArray(r.data?.data) ? r.data.data : []);
      })
      .catch(() => live && setItems([]));
    return () => {
      live = false;
    };
  }, [limit]);

  if (!items || items.length === 0) return null;

  return (
    <div data-testid="sold-showcase">
      <div className="mb-14 flex flex-wrap items-end justify-between gap-6 border-b border-charcoal/10 pb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-champagne">Sold &amp; Pending — Live From the MLS</p>
        <p className="font-serif text-lg italic text-slate-muted" data-testid="sold-count">
          {items.length} recent {items.length === 1 ? "closing" : "closings"}
        </p>
      </div>
      <div className="grid gap-x-8 gap-y-16 lg:grid-cols-12">
        {items.map((l, i) => {
          const layout = LAYOUTS[i % LAYOUTS.length];
          const img = imgOf(l);
          const href = hrefOf(l);
          return (
            <Link
              key={l.listingID || i}
              to={href}
              data-testid={`sold-card-${l.listingID || i}`}
              className={`group block ${layout.cls}`}
            >
              <div className="img-zoom relative">
                {img ? (
                  <img src={img} alt={`${l.address || "Property"}, ${l.cityName || ""} — sold`} className={`${layout.aspect} w-full object-cover`} loading="lazy" />
                ) : (
                  <div className={`${layout.aspect} w-full bg-stone-beige`} aria-hidden="true" />
                )}
                <span className="absolute left-0 top-5 bg-charcoal px-4 py-2 font-mono text-[9px] uppercase tracking-[0.3em] text-ivory">
                  Sold
                </span>
              </div>
              <div className="mt-6 flex items-start justify-between gap-6 border-t border-charcoal/10 pt-5">
                <div>
                  <p className="font-serif text-3xl font-normal text-charcoal">{l.listingPrice}</p>
                  <p className="mt-2 font-serif text-xl font-normal leading-snug text-charcoal/90">{l.address}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-muted">
                    {[l.cityName, l.stateAbrv, l.zipcode].filter(Boolean).join(", ")}
                  </p>
                  <p className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-muted">
                    <span className="flex items-center gap-1.5">
                      <BedDouble size={13} strokeWidth={1.25} aria-hidden="true" /> {l.bedrooms ?? "—"} Beds
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Bath size={13} strokeWidth={1.25} aria-hidden="true" /> {l.totalBaths ?? "—"} Baths
                    </span>
                    {l.sqFt && (
                      <span className="flex items-center gap-1.5">
                        <Ruler size={13} strokeWidth={1.25} aria-hidden="true" /> {l.sqFt} SqFt
                      </span>
                    )}
                  </p>
                </div>
                <span className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center border border-charcoal/20 text-charcoal transition-all duration-500 group-hover:border-champagne group-hover:bg-champagne group-hover:text-obsidian" aria-hidden="true">
                  <ArrowUpRight size={16} strokeWidth={1.25} />
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default SoldShowcase;
